import { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  BadgeCheck,
  BarChart3,
  Bell,
  Building2,
  CalendarRange,
  CreditCard,
  Flag,
  Gavel,
  LayoutDashboard,
  LogOut,
  Megaphone,
  Menu,
  Plug,
  Swords,
  ScrollText,
  Settings,
  ShieldAlert,
  Users,
  Video,
  Wallet,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { WhistllLogo } from "@/components/brand/WhistllLogo";
import { Wordmark } from "@/components/brand/Wordmark";
import { MfaChallengeGate } from "@/components/dashboard/MfaChallengeGate";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
}

const ADMIN_NAV: NavItem[] = [
  { to: "overview", label: "Overview", icon: LayoutDashboard },
  { to: "analytics", label: "Analytics", icon: BarChart3 },
  { to: "venues", label: "Venues", icon: Building2 },
  { to: "users", label: "Users", icon: Users },
  { to: "matches", label: "Matches", icon: Swords },
  { to: "content", label: "Content Queue", icon: Video },
  { to: "flags", label: "Flags", icon: Flag },
  { to: "disputes", label: "Disputes", icon: Gavel },
  { to: "no-shows", label: "No-shows", icon: ShieldAlert },
  { to: "payments", label: "Payments", icon: CreditCard },
  { to: "announcements", label: "Announcements", icon: Megaphone },
  { to: "audit-log", label: "Audit log", icon: ScrollText },
  { to: "settings", label: "Settings", icon: Settings },
];

const VENUE_NAV: NavItem[] = [
  { to: "overview", label: "Overview", icon: LayoutDashboard },
  { to: "analytics", label: "Analytics", icon: BarChart3 },
  { to: "courts", label: "Courts", icon: Building2 },
  { to: "bookings", label: "Bookings", icon: CalendarRange },
  { to: "payouts", label: "Payouts", icon: Wallet },
  { to: "integrations", label: "Integrations", icon: Plug },
  { to: "settings", label: "Settings", icon: Settings },
];

/**
 * Layout shared by the authenticated dashboard. Picks the right sidebar
 * nav based on the signed-in user's role so admins and venue partners
 * both live at /dashboard but see completely different content - the
 * URL never reveals which one you are.
 */
export function DashboardLayout() {
  const { profile, isAdmin, isVenueAdmin, user, signOut } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Anyone authenticated but without a recognised role lands here. We
  // don't leak "your role is unknown" via separate routes - always the
  // same URL, different content.
  if (!isAdmin && !isVenueAdmin) {
    return <NoAccessScreen email={user?.email ?? ""} onSignOut={signOut} />;
  }

  const nav = isAdmin ? ADMIN_NAV : VENUE_NAV;
  const roleLabel = isAdmin ? "Admin" : "Venue partner";

  async function onSignOut() {
    await signOut();
    navigate("/login", { replace: true });
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile sidebar toggle */}
      <button
        type="button"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open menu"
        className="fixed left-3 top-3 z-40 inline-flex h-9 w-9 items-center justify-center rounded-md border border-border bg-card text-foreground lg:hidden"
      >
        <Menu className="h-4 w-4" />
      </button>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-card transition-transform lg:static lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        {/* Brand lockup */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <WhistllLogo size={22} />
            <Wordmark size="text-lg" />
          </Link>
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground lg:hidden"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Role label */}
        <div className="px-5 pt-4 pb-1">
          <p className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-volt">
            <BadgeCheck className="h-3 w-3" />
            {roleLabel}
          </p>
        </div>

        {/* Nav items */}
        <nav className="flex-1 overflow-y-auto px-3 py-3">
          <ul className="flex flex-col gap-0.5">
            {nav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.to === "overview"}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                      isActive
                        ? "bg-volt-subtle text-foreground"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" aria-hidden />
                  <span className="truncate">{item.label}</span>
                  {item.badge && (
                    <span className="ml-auto rounded-full bg-volt px-1.5 py-0.5 text-[9px] font-black text-primary-foreground">
                      {item.badge}
                    </span>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        {/* User footer */}
        <div className="border-t border-border px-3 py-3">
          <div className="flex items-center gap-3 rounded-md px-2 py-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-volt text-xs font-black text-primary-foreground">
              {(profile?.display_name || user?.email || "?")
                .charAt(0)
                .toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold">
                {profile?.display_name || "Your account"}
              </p>
              <p className="truncate text-[11px] text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-1 flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            Sign out
          </button>
        </div>
      </aside>

      {/* Backdrop (mobile) */}
      {sidebarOpen && (
        <div
          aria-hidden
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}

      {/* Main - wrapped in the MFA gate so a verified TOTP user can't
          access dashboard content with an aal1-only session. */}
      <main className="flex-1 overflow-x-hidden">
        <MfaChallengeGate>
          <div className="mx-auto max-w-6xl px-4 py-6 sm:px-8 sm:py-10">
            <Outlet />
          </div>
        </MfaChallengeGate>
      </main>
    </div>
  );
}

function NoAccessScreen({
  email,
  onSignOut,
}: {
  email: string;
  onSignOut: () => Promise<void>;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-volt-subtle text-volt">
          <Bell className="h-5 w-5" />
        </div>
        <h1 className="text-xl font-semibold tracking-tight">
          You're signed in, but…
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          The address <span className="font-semibold text-foreground">{email}</span>{" "}
          doesn't have a dashboard assigned. Access is invite-only. If you're
          a venue partner expecting access, make sure you're using the same
          email your application was sent from. If you're a player, download
          the Whistll app to sign up.
        </p>
        <button
          type="button"
          onClick={onSignOut}
          className="mt-6 inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-5 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
